import I18n from 'i18n-js';
import React, { useRef, useContext, useState } from 'react';

import { isZeroAmount } from '@reverbdotcom/commons/src/money';
import { RCRadioCardGroup } from '@reverbdotcom/commons/src/rc/forms/RCRadioCardGroup';

import { Field, ActionType } from '../../sellFormReducer';
import { SellFormContext } from '../../SellFormContext';
import { SellFormSection } from '../SellFormSection';
import EditShippingRateCardManualPrices from '../../../../shared/edit_shipping_rate_card_manual_prices';
import { FormContext } from '../../../../shared/edit_shipping_rate_card';
import EditShippingRateCardPackage from '../../../../shared/edit_shipping_rate_card_package';
import ShippingRadioLabel from './ShippingRadioLabel';
import {
  upsertShippingRate,
  BLANK_RATE_ATTRS,
  buildBlankShippingRate,
  FREE_RATE_ATTRS,
  CARRIER_CALC_RATE_TYPE,
} from './ShippingForm';

export enum ShippingMode {
  CUSTOM = 'custom', // user manually inputs a standard shipping price (with optional combine & save price)
  CARRIER_CALCULATED = 'carrierCalculated', // user inputs package dimensions, reverb figures out the shipping cost
  FREE = 'free', // user selects simple free shipping (no combine + save; no user input required)
}

function DomesticShippingCard() {
  const { formState, dispatch, sellFormViewData, loading } = useContext(SellFormContext);
  const [touchedRadio, setTouchedRaio] = useState(false);
  const headerRef = useRef<HTMLInputElement>();

  const { currencySetting, shippingRegionSettings, address } = sellFormViewData.me.shop;
  const domesticRegion = shippingRegionSettings.find(({ isDomestic }) => isDomestic);
  const inCarrierCalculatedMode = !!formState[Field.CARRIER_CALCULATED_PACKAGE];
  const shippingRate = formState[Field.SHIPPING_RATES].find((sr) => sr.regionCode === domesticRegion.regionCode);
  const shopAddressSummary = I18n.t(
    'discovery.sellForm.shippingSection.domesticShippingCard.shopAddressSummary',
    { regionName: address.region, postalCode: address.postalCode },
  );

  const [isFreeModeSelected, setIsFreeModeSelected] = useState(
    isZeroAmount(shippingRate?.rate?.amount) &&
      !shippingRate?.incrementalRate?.amount &&
      !shippingRate?.expeditedRate?.amount,
  );

  let currentMode;
  if (domesticRegion.allowedToShipCarrierCalculated && inCarrierCalculatedMode) {
    currentMode = ShippingMode.CARRIER_CALCULATED;
  } else if (isFreeModeSelected) {
    currentMode = ShippingMode.FREE;
  } else if (shippingRate?.rate?.amount || touchedRadio) {
    currentMode = ShippingMode.CUSTOM;
  }

  function toggleCarrierCalculated() {
    if (inCarrierCalculatedMode) {
      dispatch({
        type: ActionType.DEACTIVATE_CARRIER_CALCULATED_MODE,
        shippingRates: upsertShippingRate(formState[Field.SHIPPING_RATES], buildBlankShippingRate(shippingRate.regionCode)),
      });
    } else {
      dispatch({
        type: ActionType.ACTIVATE_CARRIER_CALCULATED_MODE,
        shippingRates: upsertShippingRate(formState[Field.SHIPPING_RATES], buildBlankShippingRate(shippingRate.regionCode, CARRIER_CALC_RATE_TYPE)),
      });
    }
  }

  function handleModeChange(newMode) {
    setTouchedRaio(true);
    switch (newMode) {
      case currentMode:
        break;

      case ShippingMode.CUSTOM:
        if (inCarrierCalculatedMode) { toggleCarrierCalculated(); }
        if (isFreeModeSelected) {
          setIsFreeModeSelected(false);
          dispatch({
            type: ActionType.UPDATE_FIELD,
            fieldKey: Field.SHIPPING_RATES,
            value: upsertShippingRate(formState[Field.SHIPPING_RATES], { ...shippingRate, ...BLANK_RATE_ATTRS }),
          });
        }
        break;

      case ShippingMode.CARRIER_CALCULATED:
        toggleCarrierCalculated();
        setIsFreeModeSelected(false);
        break;

      case ShippingMode.FREE:
        if (inCarrierCalculatedMode) { toggleCarrierCalculated(); }
        setIsFreeModeSelected(true);
        dispatch({
          type: ActionType.UPDATE_FIELD,
          fieldKey: Field.SHIPPING_RATES,
          value: upsertShippingRate(formState[Field.SHIPPING_RATES], { ...shippingRate, ...FREE_RATE_ATTRS }),
        });
        break;

      default:
        break;
    }
  }

  return (
    <SellFormSection.Card>
      <div ref={headerRef} />
      <SellFormSection.FormSection
        tag="required"
        title={I18n.t(
          'discovery.sellForm.shippingSection.domesticShippingCard.header',
          { regionName: I18n.t(`discovery.shippingRegions.${shippingRate.regionCode}`) },
        )}
        fullWidthForm
      >
        <RCRadioCardGroup
          label=""
          id={`howhandle_${shippingRate.regionCode}`}
          name={`howhandle_${shippingRate.regionCode}`}
          value={currentMode}
          onChange={(evt) => handleModeChange(evt.target.value)}
          required
        >
          {domesticRegion.allowedToShipCarrierCalculated && (
            <RCRadioCardGroup.Option
              label={(
                <ShippingRadioLabel
                  shippingMode={ShippingMode.CARRIER_CALCULATED}
                  active={currentMode === ShippingMode.CARRIER_CALCULATED}
                  shopAddressSummary={shopAddressSummary}
                />)}
              value={ShippingMode.CARRIER_CALCULATED}
              disabled={loading}
            />
          )}

          <RCRadioCardGroup.Option
            label={<ShippingRadioLabel shippingMode={ShippingMode.FREE} active={currentMode === ShippingMode.FREE} />}
            value={ShippingMode.FREE}
            disabled={loading}
          />

          <RCRadioCardGroup.Option
            label={<ShippingRadioLabel shippingMode={ShippingMode.CUSTOM} active={currentMode === ShippingMode.CUSTOM} />}
            value={ShippingMode.CUSTOM}
            disabled={loading}
          />
        </RCRadioCardGroup>

        {currentMode === ShippingMode.CUSTOM && (
          <div className="bg-site-secondary ptb-1 plr-1">
            <EditShippingRateCardManualPrices
              formContext={FormContext.LISTING}
              shippingRate={shippingRate}
              handleEditShippingRate={(updated) => {
                dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.SHIPPING_RATES,
                  value: upsertShippingRate(formState[Field.SHIPPING_RATES], updated),
                });
              }}
              currency={currencySetting}
              allowedToShipExpedited={false}
              disabled={loading}
              displayShippingEstimator
            />
          </div>
        )}

        {currentMode === ShippingMode.CARRIER_CALCULATED && (
          <EditShippingRateCardPackage
            shippingPackage={formState[Field.CARRIER_CALCULATED_PACKAGE]}
            setShippingPackage={(updatedPackage) => {
              dispatch({
                type: ActionType.UPDATE_FIELD,
                fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                value: updatedPackage,
              });
            }}
            setIsInCarrierCalculatedMode={() => {
              toggleCarrierCalculated();
              headerRef.current.scrollIntoView();
            }}
            disabled={loading}
          />
        )}
      </SellFormSection.FormSection>
    </SellFormSection.Card>
  );
}

export default DomesticShippingCard;
